import { useEffect, useState } from 'react';

import { faRightToBracket, faSpinnerThird, faUser } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

import { UserSheet } from '@/components/layout/UserSheet';
import { Typography } from '@/components/Typography';
import { Button } from '@/components/ui/button';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { STORAGE_KEYS } from '@/data/constants/storage';
import { useAuthContext } from '@/data/hooks/auth';
import useUserStore from '@/data/stores/user.store';

/** Pied de la sidebar : bouton de connexion, ou identité de l'utilisateur ouvrant « Mon compte ». */
export const NavUser = () => {
  const { t } = useTranslation();
  const { user, login, isAuthenticated } = useAuthContext();
  const { closePeek } = useSidebar();
  const setIsLoggingIn = useUserStore((state) => state.setIsLoggingIn);
  // Lu dans le store (abonnement) : `user` du contexte peut venir du cache de la query
  // `/me` et ne se met pas à jour au clic, le bouton resterait sans retour visuel.
  const isLoggingIn = useUserStore((state) => state.user.isLoggingIn);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Clear login state when user is authenticated
  useEffect(() => {
    if (isAuthenticated && isLoggingIn) {
      setIsLoggingIn(false);
    }
  }, [isAuthenticated, isLoggingIn, setIsLoggingIn]);

  const handleLogin = () => {
    setIsLoggingIn(true);
    sessionStorage.setItem(STORAGE_KEYS.LOGIN_STATE_IN_PROGRESS, 'true');
    login();
  };

  if (!isAuthenticated) {
    return (
      <SidebarMenu className='p-3'>
        <SidebarMenuItem>
          <SidebarMenuButton asChild>
            <Button
              id='login-button'
              size='sm'
              className='w-full group-data-[collapsible=icon]:size-9! group-data-[collapsible=icon]:p-0!'
              disabled={isLoggingIn}
              aria-busy={isLoggingIn}
              onClick={handleLogin}
            >
              <FontAwesomeIcon
                icon={isLoggingIn ? faSpinnerThird : faRightToBracket}
                className={isLoggingIn ? 'animate-spin' : undefined}
              />
              {/* Mode replié : icône seule, le libellé reste le nom accessible du bouton. */}
              <span className='group-data-[collapsible=icon]:sr-only'>
                {isLoggingIn ? t('user.menu.logining') : t('user.menu.login')}
              </span>
            </Button>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  const label = user.fullName || t('user.menu.account');
  const subtitle = user.job || user.email;

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            size='lg'
            data-testid='user-menu-button'
            aria-label={`${t('user.menu.account')} : ${label}`}
            className='h-16 gap-2.5 rounded-none px-3 hover:bg-foreground/8 group-data-[collapsible=icon]:h-16! group-data-[collapsible=icon]:w-full! group-data-[collapsible=icon]:px-3!'
            onClick={() => {
              closePeek?.();
              setSheetOpen(true);
            }}
          >
            <div className='flex aspect-square size-9 shrink-0 items-center justify-center rounded-full bg-sidebar-primary text-sidebar-primary-foreground'>
              {user.initials ? (
                <Typography variant='paragraphSmallBold' className='text-sidebar-primary-foreground'>
                  {user.initials}
                </Typography>
              ) : (
                <FontAwesomeIcon icon={faUser} className='size-5' />
              )}
            </div>
            <div className='grid flex-1 text-left leading-tight transition-opacity duration-200 ease-linear group-data-[collapsible=icon]:opacity-0'>
              <Typography variant='paragraphSmallBold' color='foreground' className='truncate text-[13px]'>
                {label}
              </Typography>
              {subtitle ? (
                <Typography variant='paragraphMini' color='muted' className='truncate'>
                  {subtitle}
                </Typography>
              ) : null}
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
      <UserSheet open={sheetOpen} onOpenChange={setSheetOpen} />
    </>
  );
};
